import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/UI/layout/Layout";
import { SmallButton } from "../components/UI/smallButton/SmallButton";
import { media } from "../styles/GlobalStyles";
import CONFIG from "../config";
import socket from "../socket";
import { getPlayerId } from "../helpers/identity";


/**
 * Read-only spectator view for a single league series.
 * URL: /league/match/:seriesId
 *
 * Data sources:
 *   - REST GET /api/league/series/:id  — agent records, scores, games array, status
 *   - Socket `join-room` (spectator)   — for the active game room, receives live `move-made`
 *   - Socket `league-game-started`     — when next game of series begins, switch rooms
 *   - Socket `league-series-completed` — series done
 *
 * Reuses board rendering patterns from MultiplayerGame but kept lean: no inputs,
 * no timers beyond display, no Redux wiring.
 */

type CellColor = "red" | "yellow" | null;
type Board = CellColor[][];

const SERVER_API_URL = CONFIG.API_URL.replace(/\/api$/, "");

// ── Styled components ────────────────────────────────────────────────────────
const Container = styled.div`
  width: 100%;
  max-width: 100rem;
  margin: 0 auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${media.phone} {
    flex-direction: column;
    gap: 1.2rem;
  }
`;

const Title = styled.h1`
  font-size: 3.6rem;
  color: var(--color-white);
  text-shadow: 3px 3px 0 var(--color-black);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 28rem;
  gap: 2rem;
  align-items: start;

  ${media.desktopS} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--color-white);
  border: 3px solid var(--color-black);
  box-shadow: 0 0.8rem 0 var(--color-black);
  border-radius: 1.6rem;
  padding: 2rem;
`;

const PlayersRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1.2rem;
  margin-bottom: 1.6rem;
`;

const PlayerSide = styled.div<{ $align: "left" | "right"; $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) => ($align === "left" ? "flex-start" : "flex-end")};
  gap: 0.4rem;
  padding: 1rem;
  border-radius: 1rem;
  background: ${({ $active }) => ($active ? "rgba(109, 40, 217, 0.1)" : "transparent")};
  border: 2px solid ${({ $active }) => ($active ? "var(--color-purple)" : "transparent")};
  transition: all 0.2s;

  strong {
    font-size: 1.8rem;
  }
  span {
    font-size: 1.3rem;
    color: #555;
  }
`;

const SeriesScore = styled.div`
  font-size: 2.8rem;
  font-weight: bold;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const BoardWrap = styled.div`
  --cell: 7rem;
  display: inline-grid;
  grid-template-columns: repeat(7, var(--cell));
  grid-template-rows: repeat(6, var(--cell));
  gap: 0.6rem;
  background: var(--color-purple);
  padding: 1rem;
  border-radius: 1.2rem;
  border: 3px solid var(--color-black);
  box-shadow: 0 0.6rem 0 var(--color-black);
  margin: 0 auto;

  ${media.phone} {
    --cell: 4.4rem;
  }
`;

const Cell = styled.div`
  width: var(--cell);
  height: var(--cell);
  border-radius: 50%;
  background: var(--color-white);
  position: relative;
  overflow: hidden;
`;

const CellDisc = styled(motion.div)<{ $color: "red" | "yellow" }>`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: ${({ $color }) =>
    $color === "red" ? "var(--color-red)" : "var(--color-yellow)"};
  box-shadow: inset 0 -0.6rem 0 rgba(0, 0, 0, 0.15);
`;

const StatusBar = styled.div<{ $tone?: "live" | "done" | "idle" }>`
  padding: 1rem 1.4rem;
  border-radius: 1rem;
  background: ${({ $tone }) =>
    $tone === "live" ? "#dcfce7" : $tone === "done" ? "#fef3c7" : "#f3f4f6"};
  font-size: 1.4rem;
  font-weight: bold;
  text-align: center;
`;

const HistoryList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column-reverse;
  gap: 0.6rem;
  max-height: 40rem;
  overflow-y: auto;
`;

const HistoryItem = styled.li<{ $color: "red" | "yellow" }>`
  padding: 0.6rem 1rem;
  border-left: 4px solid
    ${({ $color }) => ($color === "red" ? "var(--color-red)" : "var(--color-yellow)")};
  background: #fafafa;
  font-size: 1.3rem;
  font-variant-numeric: tabular-nums;
`;

const GamesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.8rem;
`;

const GameDot = styled.span<{ $winner?: "p1" | "p2" | "tie" }>`
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  background: ${({ $winner }) =>
    $winner === "p1"
      ? "var(--color-red)"
      : $winner === "p2"
        ? "var(--color-yellow)"
        : "#d1d5db"};
  border: 2px solid var(--color-black);
`;

// ── Helpers ──────────────────────────────────────────────────────────────────
function emptyBoard(): Board {
  return Array.from({ length: 6 }, () => Array<CellColor>(7).fill(null));
}

function applyMoveImmutable(
  board: Board,
  col: number,
  row: number,
  color: "red" | "yellow",
): Board {
  return board.map((r, ri) =>
    ri === row ? r.map((cell, ci) => (ci === col ? color : cell)) : r.slice(),
  );
}

// ── Component ───────────────────────────────────────────────────────────────
type Move = { col: number; row: number; color: "red" | "yellow"; gameNumber: number };

const LeagueMatchSpectator: React.FC = () => {
  const { seriesId = "" } = useParams<{ seriesId: string }>();
  const navigate = useNavigate();

  const [series, setSeries] = useState<any>(null);
  const [board, setBoard] = useState<Board>(emptyBoard());
  const [turn, setTurn] = useState<"red" | "yellow">("red");
  const [winner, setWinner] = useState<"p1" | "p2" | "tie" | null>(null);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [history, setHistory] = useState<Move[]>([]);
  const joinedRoomRef = useRef<string | null>(null);

  // Fetch series metadata + current state
  useEffect(() => {
    if (!seriesId) return;
    const fetchSeries = async () => {
      try {
        const resp = await fetch(`${SERVER_API_URL}/api/league/series/${seriesId}`);
        if (!resp.ok) return;
        const data = await resp.json();
        setSeries(data);
      } catch (err) {
        /* ignore */
      }
    };
    fetchSeries();
    const inv = setInterval(fetchSeries, 8000);
    return () => clearInterval(inv);
  }, [seriesId]);

  // Derive the currently-active game room. Game number = (games played) + 1.
  useEffect(() => {
    if (!series) return;
    if (series.status === "COMPLETED") {
      setActiveRoomId(null);
      return;
    }
    const gamesPlayed = Array.isArray(series.games) ? series.games.length : 0;
    const currentGameNumber = gamesPlayed + 1;
    setActiveRoomId(`LEAGUE-${series.id}-G${currentGameNumber}`);
  }, [series]);

  // Join the active room as a spectator to receive live move updates
  useEffect(() => {
    if (!activeRoomId) return;
    if (!socket.connected) socket.connect();

    // Leave any previous room spectator session
    const prev = joinedRoomRef.current;
    if (prev && prev !== activeRoomId) {
      socket.emit("leave-room", { roomId: prev });
    }

    joinedRoomRef.current = activeRoomId;
    // Reset the live-board state when switching games
    setBoard(emptyBoard());
    setTurn("red");
    setWinner(null);
    setHistory([]);

    const joinHandler = (data: any) => {
      if (data?.roomState?.board) {
        setBoard(data.roomState.board);
        setTurn(data.roomState.turn ?? "red");
        setWinner(data.roomState.winner ?? null);
      }
    };
    const moveHandler = (data: any) => {
      if (typeof data?.col !== "number" || typeof data?.row !== "number") return;
      if (data.board) {
        setBoard(data.board);
      } else if (data.color) {
        setBoard((b) => applyMoveImmutable(b, data.col, data.row, data.color));
      }
      if (data.nextTurn) setTurn(data.nextTurn);
      if (data.winner !== undefined) setWinner(data.winner ?? null);
      if (data.color) {
        const gameNumber = parseInt(activeRoomId.split("-G").pop() || "1", 10);
        setHistory((h) => [...h, { col: data.col, row: data.row, color: data.color, gameNumber }]);
      }
    };
    const updateHandler = (state: any) => {
      if (!state) return;
      if (state.board) setBoard(state.board);
      if (state.turn) setTurn(state.turn);
      if (state.winner !== undefined) setWinner(state.winner ?? null);
    };

    socket.on("room-joined", joinHandler);
    socket.on("move-made", moveHandler);
    socket.on("room-updated", updateHandler);

    socket.emit("join-room", {
      roomId: activeRoomId,
      playerName: "Spectator",
      joinAs: "spectator",
      playerId: getPlayerId(),
    });

    return () => {
      socket.off("room-joined", joinHandler);
      socket.off("move-made", moveHandler);
      socket.off("room-updated", updateHandler);
      socket.emit("leave-room", { roomId: activeRoomId });
      if (joinedRoomRef.current === activeRoomId) joinedRoomRef.current = null;
    };
  }, [activeRoomId]);

  // Watch league-wide events to refresh series when games finish / new games start
  useEffect(() => {
    if (!seriesId) return;

    const onGameStarted = (data: any) => {
      if (data?.seriesId !== seriesId) return;
      // Re-fetch series to update games array and active room
      fetch(`${SERVER_API_URL}/api/league/series/${seriesId}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => d && setSeries(d))
        .catch(() => {});
    };
    const onSeriesCompleted = (data: any) => {
      if (data?.seriesId !== seriesId) return;
      fetch(`${SERVER_API_URL}/api/league/series/${seriesId}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => d && setSeries(d))
        .catch(() => {});
    };

    socket.on("league-game-started", onGameStarted);
    socket.on("league-series-completed", onSeriesCompleted);
    return () => {
      socket.off("league-game-started", onGameStarted);
      socket.off("league-series-completed", onSeriesCompleted);
    };
  }, [seriesId]);

  const bestOf = series?.league?.rules?.bestOf ?? 7;
  const gamesPlayed = Array.isArray(series?.games) ? series.games.length : 0;
  const currentGameNumber = Math.min(gamesPlayed + (series?.status === "COMPLETED" ? 0 : 1), bestOf);
  const tone: "live" | "done" | "idle" =
    !series ? "idle" : series.status === "COMPLETED" ? "done" : "live";

  const statusText = useMemo(() => {
    if (!series) return "Loading series…";
    if (series.status === "COMPLETED") {
      const champion = series.winnerId === series.p1Id ? series.p1?.name : series.p2?.name;
      return `🏆 ${champion ?? "Winner"} takes the series ${series.score1}-${series.score2}`;
    }
    return `Game ${currentGameNumber} of Bo${bestOf} — ${turn === "red" ? series.p1?.name : series.p2?.name}'s turn`;
  }, [series, currentGameNumber, bestOf, turn]);

  // Build the games-played indicator dots
  const gameDots = useMemo(() => {
    const list: ("p1" | "p2" | "tie")[] = [];
    if (Array.isArray(series?.games)) {
      for (const g of series.games) {
        if (!g?.winnerId) list.push("tie");
        else list.push(g.winnerId === series.p1Id ? "p1" : "p2");
      }
    }
    return list;
  }, [series]);

  return (
    <Layout color="purple">
      <Container>
        <Header>
          <Title>Match Spectator</Title>
          <SmallButton onClick={() => navigate("/league")}>← Back to Arena</SmallButton>
        </Header>

        <StatusBar $tone={tone}>{statusText}</StatusBar>

        <Grid>
          <Card>
            <PlayersRow>
              <PlayerSide $align="left" $active={!winner && turn === "red"}>
                <strong>{series?.p1?.name ?? "Player 1"}</strong>
                <span>🔴 Red · ELO {series?.p1?.elo ?? "—"}</span>
              </PlayerSide>
              <SeriesScore>
                {series?.score1 ?? 0} – {series?.score2 ?? 0}
              </SeriesScore>
              <PlayerSide $align="right" $active={!winner && turn === "yellow"}>
                <strong>{series?.p2?.name ?? "Player 2"}</strong>
                <span>🟡 Yellow · ELO {series?.p2?.elo ?? "—"}</span>
              </PlayerSide>
            </PlayersRow>

            {gameDots.length > 0 && (
              <GamesRow>
                {gameDots.map((w, i) => (
                  <GameDot key={i} $winner={w} title={`Game ${i + 1}`} />
                ))}
                {Array.from({ length: Math.max(0, bestOf - gameDots.length) }).map((_, i) => (
                  <GameDot key={`pending-${i}`} title="Upcoming" />
                ))}
              </GamesRow>
            )}

            <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
              <BoardWrap>
                <AnimatePresence>
                  {board.map((row, ri) =>
                    row.map((cell, ci) => (
                      <Cell key={`${ri}-${ci}`}>
                        {cell && (
                          <CellDisc
                            $color={cell}
                            initial={{ y: -400 }}
                            animate={{ y: 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 22 }}
                          />
                        )}
                      </Cell>
                    )),
                  )}
                </AnimatePresence>
              </BoardWrap>
            </div>

            {winner && (
              <div style={{ textAlign: "center", marginTop: "1.6rem", fontSize: "1.6rem" }}>
                {winner === "tie"
                  ? "🤝 Game drawn"
                  : `✅ ${winner === "p1" ? series?.p1?.name : series?.p2?.name} wins this game`}
              </div>
            )}
          </Card>

          <Card>
            <h3 style={{ marginBottom: "1.2rem", fontSize: "1.8rem" }}>Move history</h3>
            {history.length === 0 ? (
              <p style={{ color: "#888", fontSize: "1.3rem" }}>Waiting for first move…</p>
            ) : (
              <HistoryList>
                {history.map((m, i) => (
                  <HistoryItem key={i} $color={m.color}>
                    #{i + 1} · {m.color === "red" ? series?.p1?.name : series?.p2?.name} →
                    col {m.col + 1}
                  </HistoryItem>
                ))}
              </HistoryList>
            )}
          </Card>
        </Grid>
      </Container>
    </Layout>
  );
};

export default LeagueMatchSpectator;
